"use strict";

import { BRIDGE_VERSION } from "./version.js";

/**
 * PCA_BRIDGE_BUS_INIT — the script injected via
 * `injectedJavaScriptBeforeContentLoaded` before any web content loads.
 *
 * Installs `window.__pcaBridge` in the WebView's JS context. The bus:
 *   - is per-WebView-context-by-construction (each WebView has its own JS
 *     context; the global lives only in that context)
 *   - accepts multiple registered handlers per reserved message type so
 *     multiple Bridge instances in the same WebView can coexist
 *   - buffers messages of a type with no registered handlers for up to 5s
 *   - pings native with `__bridge/busReady` after install so the native
 *     transport can flush its outgoing queue and trigger `reannounceLocal()`
 *
 * The bus runs as an IIFE so consumers can dispatch and register against it
 * immediately. Internal state (buffers, handlers) is closure-private.
 */

export const PCA_BRIDGE_BUS_INIT = `
(function () {
  if (typeof window === 'undefined') return
  if (window.__pcaBridge) return  // idempotent in case it's injected twice

  var BRIDGE_VERSION = ${JSON.stringify(BRIDGE_VERSION)}
  var BUFFER_TTL_MS = 5000
  var handlers = Object.create(null)  // type -> Array<fn>
  var buffer = Object.create(null)    // type -> Array<{msg, receivedAt}>
  var logger = {
    debug: function () {},
    warn: function (msg, ctx) {
      try { console.warn('[pca-bridge]', msg, ctx) } catch (e) {}
    },
    error: function (msg, ctx) {
      try { console.error('[pca-bridge]', msg, ctx) } catch (e) {}
    }
  }

  function now() { return Date.now() }

  function sweepBuffer() {
    var cutoff = now() - BUFFER_TTL_MS
    var dropped = []
    var hasAny = false
    for (var type in buffer) {
      var entries = buffer[type]
      var kept = []
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].receivedAt >= cutoff) {
          kept.push(entries[i])
        } else {
          dropped.push({ type: type, ageMs: now() - entries[i].receivedAt })
        }
      }
      if (kept.length === 0) delete buffer[type]
      else {
        buffer[type] = kept
        hasAny = true
      }
    }
    if (dropped.length > 0 && window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: '__bridge/drop',
          bridgeVersion: BRIDGE_VERSION,
          messages: dropped
        }))
      } catch (e) {}
    }
    // Stop ticking when the buffer drains; ensureSweep() restarts on demand.
    if (!hasAny && sweepInterval !== null) {
      clearInterval(sweepInterval)
      sweepInterval = null
    }
  }

  var sweepInterval = null
  function ensureSweep() {
    if (sweepInterval !== null) return
    sweepInterval = setInterval(sweepBuffer, 1000)
  }

  function register(type, fn) {
    if (!handlers[type]) handlers[type] = []
    handlers[type].push(fn)
    // Drain any buffered messages for this type, applying the same TTL the
    // background sweep uses so entries that already expired don't get
    // delivered just because sweep hasn't run yet.
    var pending = buffer[type]
    if (pending && pending.length > 0) {
      delete buffer[type]
      var cutoff = now() - BUFFER_TTL_MS
      for (var i = 0; i < pending.length; i++) {
        if (pending[i].receivedAt < cutoff) continue
        try { fn(pending[i].msg) } catch (e) {
          logger.error('Handler threw while draining buffer', { type: type, error: e })
        }
      }
    }
  }

  function unregister(type, fn) {
    var arr = handlers[type]
    if (!arr) return
    var idx = arr.indexOf(fn)
    if (idx >= 0) arr.splice(idx, 1)
    if (arr.length === 0) delete handlers[type]
  }

  function dispatch(msg) {
    if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return
    var type = msg.type
    var arr = handlers[type]
    if (arr && arr.length > 0) {
      // Snapshot the array to avoid issues if a handler registers/unregisters during dispatch
      var snapshot = arr.slice()
      for (var i = 0; i < snapshot.length; i++) {
        try { snapshot[i](msg) } catch (e) {
          logger.error('Handler threw during dispatch', { type: type, error: e })
        }
      }
    } else {
      if (!buffer[type]) buffer[type] = []
      buffer[type].push({ msg: msg, receivedAt: now() })
      ensureSweep()
    }
  }

  function setLogger(next) {
    // Validate both methods the bus actually calls; a partial logger would
    // throw TypeError when dispatch/register tries to invoke a missing one.
    if (next && typeof next.warn === 'function' && typeof next.error === 'function') {
      logger = next
    }
  }

  window.__pcaBridge = {
    register: register,
    unregister: unregister,
    dispatch: dispatch,
    setLogger: setLogger
  }

  // Ping native: the bus is ready in this JS context. Native transport
  // listens for this to (a) flush its outgoing queue and (b) call
  // bridge.reannounceLocal() so the freshly-loaded web context learns about
  // already-exposed local actions/values.
  try {
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: '__bridge/busReady', bridgeVersion: BRIDGE_VERSION }))
    }
  } catch (e) {}
})();
true;
`;
//# sourceMappingURL=bus_init.js.map